
import type { ReferentFormData } from '@/types/referentForm';
import { FingerprintConsentCheckbox } from '@/components/forms/FingerprintConsentCheckbox';
import { Loader2 } from 'lucide-react';
import { BrandedBackground, BrandedText, BrandedIcon } from '@/components/shared/ui';

interface BrandingSettings {
  enabled: boolean;
  logo_url?: string;
  primary_color?: string;
}

interface ConfirmationStepProps {
  candidateName: string;
  formData: ReferentFormData;
  fingerprintConsent: boolean;
  setFingerprintConsent: (consent: boolean) => void;
  isGeneratingFingerprint?: boolean;
  fingerprintError?: string | null;
  branding?: BrandingSettings | null;
}

export const ConfirmationStep = ({ 
  candidateName, 
  formData,
  fingerprintConsent,
  setFingerprintConsent,
  isGeneratingFingerprint = false,
  fingerprintError,
  branding
}: ConfirmationStepProps) => {
  // Fonction pour formater les dates ISO en format français DD/MM/YYYY
  const formatDate = (isoDate: string) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    return date.toLocaleDateString('fr-FR');
  };

  // Fonction pour obtenir le label de relation
  const getRelationLabel = (relationType: string) => {
    const relationLabels: { [key: string]: string } = {
      'manager': 'Manager',
      'collègue': 'Collègue',
      'client': 'Client',
      'autre': 'Autre'
    };
    return relationLabels[relationType] || relationType;
  };

  return (
    <div className="space-y-8 font-['Bricolage_Grotesque']">
      {/* Description avec nom candidat dynamique */}
      <div className="text-center">
        <p className="text-gray-600">
          Veuillez vérifier vos réponses avant de soumettre votre évaluation pour {candidateName}.
        </p>
      </div>

      {/* Résumé de l'évaluation - Layout 2 colonnes */}
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-gray-900">Résumé de votre évaluation</h3>
        
        <div className="space-y-4">
          {/* Type de relation */}
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600">Type de relation :</span>
            <span className="font-medium text-gray-900">
              {getRelationLabel(formData.relationTypeConfirmed)}
            </span>
          </div>
          
          {/* Période de collaboration */}
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600">Période de collaboration :</span>
            <span className="font-medium text-gray-900">
              {formData.collaborationEndDate 
                ? `${formatDate(formData.collaborationStartDate)} - ${formatDate(formData.collaborationEndDate)}`
                : `Depuis le ${formatDate(formData.collaborationStartDate)} (en cours)`
              }
            </span>
          </div>
          
          {/* Évaluations */}
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600">Évaluation :</span>
            <span className="font-medium text-gray-900">
              {Object.keys(formData.ratings).length} critères évalués
            </span>
          </div>
          
          {/* Commentaire audio */}
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600">Commentaire audio :</span>
            <BrandedText 
              className="font-medium flex items-center gap-1"
              branding={branding}
              variant="success"
            >
              {(formData.audioFileUrl || formData.audioTranscription) ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Enregistré
                </>
              ) : (
                <span className="text-red-600">❌ Manquant</span>
              )}
            </BrandedText>
          </div>
          
          {/* Document signé */}
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600">Document signé :</span>
            <BrandedText 
              className="font-medium flex items-center gap-1"
              branding={branding}
              variant="success"
            >
              {formData.signedDocumentUrl ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Signé
                </>
              ) : (
                <span className="text-red-600">❌ Manquant</span>
              )}
            </BrandedText>
          </div>
        </div>
      </div>

      {/* Consentement fingerprinting */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Sécurité et confidentialité</h3>
        
        <FingerprintConsentCheckbox
          checked={fingerprintConsent}
          onCheckedChange={setFingerprintConsent}
          className="w-full"
        />

        {/* Erreur fingerprinting */}
        {fingerprintError && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-yellow-800 text-sm">
              ⚠️ Fingerprinting non disponible: {fingerprintError}
            </p>
            <p className="text-yellow-700 text-xs mt-1">
              Vous pouvez continuer sans cette fonctionnalité.
            </p>
          </div>
        )}

        {/* Statut fingerprinting */}
        {fingerprintConsent && isGeneratingFingerprint && (
          <BrandedBackground
            branding={branding}
            opacity={0.1}
            withBorder={true}
            className="rounded-lg p-3"
          >
            <div className="flex items-center gap-2">
              <BrandedIcon 
                Icon={Loader2} 
                branding={branding}
                className="h-4 w-4 animate-spin"
              />
              <BrandedText 
                className="text-sm"
                branding={branding}
                as="p"
              >
                Génération de l'empreinte device...
              </BrandedText>
            </div>
          </BrandedBackground>
        )}
      </div>

      {/* Encadré Important avec bordure rouge */}
      <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
        <h4 className="font-semibold text-red-600 mb-2">Important</h4>
        <p className="text-red-700">
          Une fois soumise, votre évaluation ne peut être modifiée. Veuillez vous assurer que 
          toutes les informations sont correctes.
        </p>
      </div>
    </div>
  );
};
